import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService, shiftsService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

export function VolunteerLoginPage() {
  const WAITLIST_MODAL_DISMISSED_KEY = 'volunteer_login_waitlist_modal_dismissed';
  const SIGNUP_CLOSED_MODAL_DISMISSED_KEY = 'volunteer_login_signup_closed_modal_dismissed';
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [eventName, setEventName] = useState<string>('Roskilde Festival');
  const [showWaitlistModalSetting, setShowWaitlistModalSetting] = useState(false);
  const [showSignupClosedModalSetting, setShowSignupClosedModalSetting] = useState(false);
  const [waitlistModalDismissed, setWaitlistModalDismissed] = useState(false);
  const [signupClosedModalDismissed, setSignupClosedModalDismissed] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setWaitlistModalDismissed(sessionStorage.getItem(WAITLIST_MODAL_DISMISSED_KEY) === '1');
    setSignupClosedModalDismissed(sessionStorage.getItem(SIGNUP_CLOSED_MODAL_DISMISSED_KEY) === '1');

    shiftsService.getSettings().then((response) => {
      if (response?.data?.event_name) {
        setEventName(response.data.event_name);
      }
      setShowWaitlistModalSetting(response?.data?.volunteer_login_waitlist_modal_enabled === true);
      setShowSignupClosedModalSetting(response?.data?.volunteer_login_new_signups_closed === true);
    }).catch(() => {
      // Use default if settings fetch fails
    });
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await authService.requestLogin(email);

      // In dev bypass mode the backend returns a JWT and the token; prefer immediate login
      const jwtToken = res.data?.jwtToken;
      if (jwtToken) {
        localStorage.setItem('token', jwtToken);
        navigate('/');
        return;
      }

      // Otherwise, use the token-based flow
      const token = res.data?.loginToken;
      if (token) {
        navigate(`/login/${token}`);
        return;
      }

      setSent(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send login link');
    } finally {
      setLoading(false);
    }
  };

  const dismissWaitlistModal = () => {
    sessionStorage.setItem(WAITLIST_MODAL_DISMISSED_KEY, '1');
    setWaitlistModalDismissed(true);
  };

  const dismissSignupClosedModal = () => {
    sessionStorage.setItem(SIGNUP_CLOSED_MODAL_DISMISSED_KEY, '1');
    setSignupClosedModalDismissed(true);
  };

  const showSignupClosedModal = showSignupClosedModalSetting && !signupClosedModalDismissed && !sent;
  const showWaitlistModal = showWaitlistModalSetting && !waitlistModalDismissed && !sent && !showSignupClosedModal;

  if (sent) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: 'url(/rf24bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange-400/80 via-pink-500/80 to-purple-600/80"></div>
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full relative z-10">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">📧</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Check Your Inbox!</h2>
            <p className="text-gray-600">
              We've sent a magic link to <span className="font-semibold text-purple-600">{email}</span>
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3">What's next?</h3>
            <ol className="text-gray-700 space-y-2 list-decimal list-inside">
              <li>Check your email inbox (and spam folder just in case)</li>
              <li>Click the secure login link we sent you</li>
              <li>You'll be automatically logged in and ready to go!</li>
            </ol>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              💡 <strong>Tip:</strong> The link expires in 24 hours. If you don't see the email, check your spam folder or try again.
            </p>
          </div>

          <button
            onClick={() => {
              setSent(false);
              setEmail('');
            }}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
          >
            ← Try a Different Email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: 'url(/rf24bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-orange-400/80 via-pink-500/80 to-purple-600/80"></div>
      {showSignupClosedModal && (
        <div className="fixed inset-0 bg-black/45 z-20 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 border-2 border-amber-200">
            <h2 className="text-2xl font-bold text-amber-900 mb-3">We are not accepting new volunteers right now</h2>
            <p className="text-sm text-amber-900 mb-3">
              New volunteer signups are currently closed.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              If you want to explore other ways to get involved, please check the official volunteer positions at Roskilde Festival:
            </p>
            <p className="text-sm text-gray-700 mb-4">
            <a
              href="https://www.roskilde-festival.dk/en/become-a-volunteer/volunteer-positions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900 underline"
            >
              View official RF volunteer positions
            </a>
            </p>
             <p className="text-sm text-gray-700">
In case you are already registered as a volunteer with us, you can login here: <a href="/login" className="text-sm font-semibold text-blue-700 hover:text-blue-900 underline">Volunteer Login</a>
            </p>
          </div>
        </div>
      )}
      {showWaitlistModal && (
        <div className="fixed inset-0 bg-black/45 z-20 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 border-2 border-yellow-200">
            <h2 className="text-2xl font-bold text-yellow-900 mb-3">Heads up: long waiting list in our team</h2>
            <p className="text-sm text-yellow-900 mb-3">
              We currently have a long waiting list for this team.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              If you want better chances of getting a spot, please check out other volunteer teams at Roskilde Festival:
            </p>
            <a
              href="https://www.roskilde-festival.dk/en/become-a-volunteer/volunteer-positions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900 underline"
            >
              View official RF volunteer positions
            </a>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={dismissWaitlistModal}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium"
              >
                I still want to apply here
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-3xl w-full relative z-10">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎪</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Volunteer at {eventName}</h1>
          <p className="text-lg text-gray-600">Join Our Team of Festival Volunteers!</p>
        </div>

        <div className="mb-8 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">🎉 Welcome to the Team!</h2>
          <p className="text-gray-700 mb-4">
            Thank you for your interest in supporting Roskilde Festival as a volunteer! We are excited to have you in our team and can't wait for what lies ahead!
          </p>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">🚪 Your Role</h3>
              <p className="text-gray-700">
                You'll be helping us check tickets and access permits at the festival gates. You'll work <strong>4 shifts in total</strong>, each lasting 8 hours (+ break), from <strong>Saturday, 27 June 2026</strong> to <strong>Sunday, 05 July 2026</strong>.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">🎁 What You Get</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Free access to the entire festival</li>
                <li>Entry to the special Volunteer Village</li>
                <li>Optional volunteer camping with clean facilities</li>
                <li>Free food vouchers and drinks for each shift</li>
              </ul>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
            <p className="text-xs text-gray-500 mt-2">
              We'll send you a secure link - no password needed!
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-500 transition-all shadow-lg transform hover:scale-105 disabled:transform-none"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
            ) : (
              '🌟 Start Your Application'
            )}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="text-center mb-4">
            <p className="text-sm text-gray-600">
              Questions? Contact us at <a href="mailto:roskilde@connectingworlds.dk" className="text-purple-600 hover:text-purple-700 font-semibold underline">roskilde@connectingworlds.dk</a>
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Admin or supervisor? <Link to="/login" className="text-purple-600 hover:text-purple-700 font-semibold underline">Use standard login</Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            🔒 Your data is secure and will only be used for volunteer management.
          </p>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            Connecting Worlds DK (CVR: 43927434) is a volunteer association, registered in Denmark.
          </p>
        </div>
      </div>
    </div>
  );
}
